import { useEffect, useState } from "react";
import { Activity, Wifi, LogOut, Sun, Moon } from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import AccountPanel from "@/components/cot/AccountPanel";
import AlertsBell from "@/components/cot/AlertsBell";
import DataHealthIndicator from "@/components/cot/DataHealthIndicator";

export default function TopBar({ reportDate, loading }) {
  const { logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  const time = now.toISOString().replace("T", " ").slice(0, 19) + " UTC";

  return (
    <header
      className="border-b border-zinc-800 bg-black/95 backdrop-blur sticky top-0 z-20"
      data-testid="top-bar"
    >
      <div className="px-3 lg:px-6 py-2 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="inline-block h-2 w-2 bg-amber-500 blink" aria-hidden />
            <span className="text-sm font-semibold tracking-[0.14em] text-amber-term">
              COT · The MacroDesk
            </span>
          </div>
          <span className="hidden md:inline text-[11px] uppercase tracking-widest text-zinc-500">
            Macro · Rates · Positioning
          </span>
          <Link
            to="/dxy"
            data-testid="nav-dxy-wrap"
            className="text-[11px] uppercase tracking-widest text-amber-term border border-amber-500/40 hover:bg-amber-500/10 px-2 py-0.5 transition-colors"
            title="Daily DXY session wrap (updates 10PM Dubai)"
          >
            DXY Wrap →
          </Link>
        </div>

        <div className="flex items-center gap-4 text-[11px] uppercase tracking-widest text-zinc-400">
          <div className="hidden sm:flex items-center gap-1.5" data-testid="report-date">
            <span className="text-zinc-600">REPORT</span>
            <span className="text-zinc-100">{reportDate || "—"}</span>
          </div>
          <div className="flex items-center gap-1.5" data-testid="system-time">
            <Activity size={12} strokeWidth={1.5} className="text-zinc-500" />
            <span className="text-zinc-100">{time}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Wifi
              size={12}
              strokeWidth={1.5}
              className={loading ? "text-amber-500 animate-pulse" : "text-emerald-500"}
            />
            <span className={loading ? "text-amber-term" : "text-pos"}>
              {loading ? "SYNC" : "LIVE"}
            </span>
          </div>
          <AccountPanel />
          <DataHealthIndicator />
          <AlertsBell />
          <button
            type="button"
            onClick={toggleTheme}
            data-testid="theme-toggle-button"
            className="flex items-center gap-1.5 text-zinc-500 hover:text-amber-term transition-colors"
            title={theme === "light" ? "Switch to dark mode" : "Switch to light mode"}
          >
            {theme === "light" ? (
              <Moon size={12} strokeWidth={1.5} />
            ) : (
              <Sun size={12} strokeWidth={1.5} />
            )}
            <span className="hidden sm:inline">{theme === "light" ? "DARK" : "LIGHT"}</span>
          </button>
          <button
            type="button"
            onClick={logout}
            data-testid="logout-button"
            className="flex items-center gap-1.5 text-zinc-500 hover:text-amber-term transition-colors"
            title="Log out"
          >
            <LogOut size={12} strokeWidth={1.5} />
            <span className="hidden sm:inline">LOGOUT</span>
          </button>
        </div>
      </div>
    </header>
  );
}
